import { useEffect, useState } from 'react';
import { useAwmsStore } from '@/store';
import { useHandleError, useSchoolList, useSchoolParameters } from '@/hooks';
import { updateFilters } from './utils/updateFilters';

export const useAdminData = () => {
  const handleError = useHandleError();
  const activeYear = useAwmsStore((state) => state.activeYear);
  const selectedSchoolId = useAwmsStore((state) => state.selectedSchoolId);
  const setSelectedSchoolId = useAwmsStore((state) => state.setSelectedSchoolId);
  const setSearchFilters = useAwmsStore((state) => state.setSearchFilters);
  const searchFilters = useAwmsStore((state) => state.searchFilters);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const { data: schoolList = [], isLoading: isSchoolListLoading, error: schoolListError } = useSchoolList(activeYear);
  const {
    data: schoolParams,
    isLoading: isSchoolParamsLoading,
    error: schoolParamsError,
  } = useSchoolParameters(selectedSchoolId, activeYear);

  useEffect(() => {
    if (!selectedSchoolId && schoolList) {
      updateFilters(schoolList, selectedSchoolId, activeYear, searchFilters, setSelectedSchoolId, setSearchFilters);
    }
  }, [selectedSchoolId, schoolList, activeYear, setSearchFilters]);

  useEffect(() => {
    if (schoolListError || schoolParamsError) {
      console.error('Error fetching data:', { schoolListError, schoolParamsError });
    }
    handleError([schoolListError]);
  }, [schoolListError, schoolParamsError, handleError]);

  // This ensures that the isPageLoading state is correctly set based on selectedSchoolId and activeYear.
  // Due to React's hook rules, hooks must be called unconditionally and in the same order on every render.
  // As a result, we cannot conditionally call useSchoolParameters (e.g. if selectedSchoolId > 0),
  // which can lead to the isLoading state being incorrectly set for these hooks.
  // Note: Although isSchoolParamsLoading always return true when selectedSchoolId is 0,
  // the queries do not actually fetch data because the enabled option is set to false in that case.
  const shouldRefetch = selectedSchoolId > 0 && activeYear > 0;

  useEffect(() => {
    setIsPageLoading(isSchoolListLoading || (isSchoolParamsLoading && shouldRefetch));
  }, [isSchoolListLoading, isSchoolParamsLoading]);

  return {
    schoolList,
    schoolParams,
    isPageLoading,
  };
};
